import { initializeApp } from "firebase/app";
import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getDocs,
    getFirestore,
    Timestamp,
    updateDoc,
} from "firebase/firestore";
import { getAuth, GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { IProject } from "../interfaces/project.interface";
import { ICategory } from "../interfaces/category.interface";
import { IPeriod } from "../interfaces/period.interface";
import { INewProject } from "../interfaces/new-project.interface";
import { INewCategory } from "../interfaces/new-category.interface";
import { INewPeriod } from "../interfaces/new-period.interface";

const firebaseConfig = {
    apiKey: "AIzaSyBjwA2OrTE85a3GjSpsDG2JmBU8RafcUYg",
    authDomain: "organisoriser.firebaseapp.com",
    projectId: "organisoriser",
    storageBucket: "organisoriser.firebasestorage.app",
    messagingSenderId: "999598043170",
    appId: "1:999598043170:web:5e9f631549b6e34bc91a7b",
};
const app = initializeApp(firebaseConfig);

const authProvider = new GoogleAuthProvider();
const auth = getAuth();

auth.onAuthStateChanged((user) => {
    if (!user) {
        signInWithPopup(auth, authProvider).then(() => {
            window.location.reload();
        });
    }
});

export const getProjects: () => Promise<IProject[]> = async () => {
    if (!auth.currentUser) {
        return;
    }

    const db = getFirestore(app);

    const projectsRef = collection(db, "projects");
    const projectsResp = await getDocs(projectsRef);

    let projects = projectsResp.docs
        .map((d) => {
            let proj = d.data() as IProject;
            proj.id = d.id;

            return proj;
        })
        .sort((a, b) => (a.title < b.title ? -1 : 1));

    return projects;
};

export const getProjectCategories = async (id: string): Promise<ICategory[]> => {
    const db = getFirestore(app);

    const categoriesRef = collection(db, "projects", id, "categories");
    const categoriesResp = await getDocs(categoriesRef);

    let categories = await Promise.all(
        categoriesResp.docs.map(async (c) => {
            let cat = c.data() as ICategory;
            cat.id = c.id;

            let periodsRef = collection(db, "projects", id, "categories", cat.id, "periods");
            let periodsResp = await getDocs(periodsRef);

            cat.periods = periodsResp.docs
                .map((p) => {
                    let per = p.data() as IPeriod;
                    per.id = p.id;

                    return per;
                })
                .sort((p1, p2) => p1.startDateTime.toMillis() - p2.startDateTime.toMillis());

            return cat;
        }),
    );

    return categories.sort((a, b) => (a.orderId ?? 10000) - (b.orderId ?? 10000));
};

export const createProject = async (project: INewProject): Promise<IProject> => {
    const db = getFirestore(app);

    const projectRef = collection(db, "projects");
    const projectResp = await addDoc(projectRef, project);

    let output = project as IProject;
    output.id = projectResp.id;

    return output;
};

export const createCategory = async (
    projectId: string,
    category: INewCategory,
): Promise<ICategory> => {
    const db = getFirestore(app);

    const categoryRef = collection(db, "projects", projectId, "categories");
    const categoryResp = await addDoc(categoryRef, category);

    let output = category as ICategory;
    output.id = categoryResp.id;
    output.periods = [];

    return output;
};

export const createPeriod = async (
    projectId: string,
    categoryId: string,
    period: INewPeriod,
): Promise<IPeriod> => {
    const db = getFirestore(app);

    const periodRef = collection(db, "projects", projectId, "categories", categoryId, "periods");
    const periodResp = await addDoc(periodRef, period);

    let output = period as IPeriod;
    output.id = periodResp.id;

    return output;
};

export const updateCategory = async (
    projectId: string,
    category: ICategory,
): Promise<ICategory> => {
    const db = getFirestore(app);

    const categoryRef = doc(db, "projects", projectId, "categories", category.id);
    await updateDoc(categoryRef, { ...category });

    return category;
};

export const updatePeriod = async (
    projectId: string,
    categoryId: string,
    period: IPeriod,
): Promise<IPeriod> => {
    const db = getFirestore(app);

    const periodRef = doc(
        db,
        "projects",
        projectId,
        "categories",
        categoryId,
        "periods",
        period.id,
    );
    await updateDoc(periodRef, { ...period });

    return period;
};

export const deleteProject = async (projectId: string): Promise<any> => {
    const db = getFirestore(app);

    const projectRef = doc(db, "projects", projectId);
    await deleteDoc(projectRef);
};

export const deleteCategory = async (projectId: string, categoryId: string): Promise<any> => {
    const db = getFirestore(app);

    const categoryRef = doc(db, "projects", projectId, "categories", categoryId);
    await deleteDoc(categoryRef);
};

export const deletePeriod = async (
    projectId: string,
    categoryId: string,
    periodId: string,
): Promise<any> => {
    const db = getFirestore(app);

    const periodRef = doc(db, "projects", projectId, "categories", categoryId, "periods", periodId);
    await deleteDoc(periodRef);
};

const timestampToDateString = (timestamp: Timestamp) => {
    let date = timestamp.toDate();
    date.setSeconds(0);
    date.setMilliseconds(0);

    let monthString = (date.getMonth() < 9 ? "0" : "") + (date.getMonth() + 1);
    let dayString = (date.getDate() < 9 ? "0" : "") + date.getDate();

    let dateString = `${dayString}/${monthString}/${date.getFullYear()}`;

    let hourString: string = (date.getHours() < 10 ? "0" : "") + date.getHours();
    let minsString: string = (date.getMinutes() < 10 ? "0" : "") + date.getMinutes();

    let timeString = `${hourString}:${minsString}`;

    return `${dateString} ${timeString}`;
};

export const createProjectCsv = async (
    project: IProject,
    categories: ICategory[],
    startDate?: Date,
    endDate?: Date,
) => {
    const isInRange = (period: IPeriod): boolean => {
        console.log(period.title);
        if (!period.endDateTime) return false;

        let periodStartDate = period.startDateTime.toDate();
        let periodEndDate = period.endDateTime.toDate();

        let checkingStartDate = !startDate ? periodStartDate : startDate;
        let checkingEndDate = !endDate ? periodEndDate : endDate;

        return checkingStartDate <= periodStartDate && checkingEndDate >= periodEndDate;
    };

    let titleRow = "Assigned To,Title,Start Date,End Date,Duration (hrs),Chargable\n";

    let csv = titleRow;
    for (let cat of categories) {
        for (let period of cat.periods.filter((p) => isInRange(p))) {
            console.log(period.title);
            let duration = period.endDateTime.toMillis() - period.startDateTime.toMillis();
            duration = Math.round((duration / 1000 / 60 / 60) * 100) / 100;
            let newRow =
                period.assignedTo +
                "," +
                (period.title === cat.title ? period.title : `${cat.title} - ${period.title}`) +
                "," +
                timestampToDateString(period.startDateTime) +
                "," +
                timestampToDateString(period.endDateTime) +
                "," +
                duration +
                "," +
                (period.chargable ? "Yes" : "No") +
                "\n";

            csv += newRow;
        }
    }

    var blob = new Blob([csv], { type: "text/csv" });
    var url = window.URL.createObjectURL(blob);

    var a = document.createElement("a");
    a.href = url;
    a.download = project.title + ".csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
};
